// A Svelte 5 view of a store (a `Program`, or anything with the store
// contract) for `.svelte.ts` classes, where `$store` is not available:
//
//   class Summaries {
//     private program = new Program<State, Msg>({ init, update });
//     private view = toState(this.program);
//     get size() { return Object.keys(this.view.current.entries).length; }
//   }
//
// Reading `current` in a component, `$derived` or `$effect` tracks it: the
// store is subscribed to while something reads it reactively, and let go
// by itself when the last such reader goes (Svelte's `createSubscriber`).
// A read anywhere else gives the store's state at that moment.
import { createSubscriber } from 'svelte/reactivity';

/** Anything with the Svelte store contract. */
export interface Readable<S> {
  subscribe(run: (s: S) => void): () => void;
}

export interface StateView<S> {
  readonly current: S;
}

export function toState<S>(store: Readable<S>): StateView<S> {
  let value!: S;
  let live = false;
  const tracked = createSubscriber((update) => {
    live = true;
    // The store calls back at once with the state the reader already has: only later calls are changes.
    let first = true;
    const stop = store.subscribe((s) => {
      value = s;
      if (!first) update();
    });
    first = false;
    return () => {
      live = false;
      stop();
    };
  });
  return {
    get current() {
      tracked();
      // Not subscribed (read outside an effect): read the state once.
      if (!live) store.subscribe((s) => (value = s))();
      return value;
    },
  };
}
